/*
 * Purpose: Contextual configuration editor — shared panel for every Mission block
 * Last modified: 2026-06-27 | Agent
 * Completeness: 95
 */
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { Node } from '@/stores/workflowStore';
import {
  CONTEXTUAL_CONFIGURATION_HELP,
  IO_DIRECTION_HINTS,
  SWISS_ARMY_BLOCK_HELP,
  type BlockIoDirection,
} from '@/lib/blockContext';
import { getBlockTypeLabel } from '@/lib/blockTypes';
import { WandSparkles } from 'lucide-react';
import { useWorkflowStore } from '@/stores/workflowStore';
import { toast } from 'sonner';
import { getSmartBlockInputRequirements } from '@/services/blockAutoConfigure';

interface BlockContextualConfigPanelProps {
  node: Node;
  onUpdate: (key: string, value: unknown) => void;
  onOpenSmartInput?: () => void;
}

function configText(value: unknown): string {
  if (value == null) return '';
  return String(value);
}

export function BlockContextualConfigPanel({ node, onUpdate, onOpenSmartInput }: BlockContextualConfigPanelProps) {
  const autoConfigureNode = useWorkflowStore((s) => s.autoConfigureNode);
  const currentWorkflow = useWorkflowStore((s) => s.currentWorkflow);
  const typeLabel = getBlockTypeLabel(node.type, node.subtype);
  const showIo =
    node.type === 'io' ||
    node.type === 'agent' ||
    node.type === 'drone' ||
    node.type === 'trigger' ||
    (node.type === 'tool' && node.subtype === 'http') ||
    (node.type === 'action' && (node.subtype === 'webhook' || node.subtype === 'notification'));

  const ioDirection = configText(node.config.ioDirection) as BlockIoDirection | '';

  return (
    <Card className="p-3 space-y-3 border-primary/20 bg-primary/5">
      <div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-foreground">Contextual configuration</p>
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 px-2 text-[11px]"
            onClick={() => {
              const changes = autoConfigureNode(node.id);
              const latestNode =
                currentWorkflow?.nodes.find((n) => n.id === node.id) ?? node;
              const needsInput = getSmartBlockInputRequirements(latestNode).length > 0;
              if (changes.length > 0) toast.success('Smart Blocks configured this block');
              else toast.message('Block already configured');
              if (needsInput) onOpenSmartInput?.();
            }}
          >
            <WandSparkles className="h-3.5 w-3.5" />
            Smart Blocks
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">{SWISS_ARMY_BLOCK_HELP}</p>
        <p className="text-xs text-muted-foreground">{CONTEXTUAL_CONFIGURATION_HELP}</p>
        {getSmartBlockInputRequirements(node).length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 mt-2 px-2 text-[11px]"
            onClick={onOpenSmartInput}
          >
            Open required inputs
          </Button>
        )}
      </div>

      <div>
        <Label className="text-sm">Label in this Mission</Label>
        <Input
          placeholder={typeLabel}
          value={configText(node.config.contextLabel)}
          onChange={(e) => onUpdate('contextLabel', e.target.value)}
          className="mt-1 text-xs"
        />
        <p className="text-[10px] text-muted-foreground mt-1">Engine type: {typeLabel}</p>
      </div>

      <div>
        <Label className="text-sm">Purpose here</Label>
        <textarea
          placeholder="What should this block do in this flow?"
          value={configText(node.config.contextPurpose)}
          onChange={(e) => onUpdate('contextPurpose', e.target.value)}
          className="w-full mt-1 text-xs p-2 rounded border border-input bg-background min-h-[72px]"
          rows={3}
        />
      </div>

      {showIo && (
        <div>
          <Label className="text-sm">IO direction</Label>
          <select
            className="mt-1 w-full text-xs border rounded-md px-2 py-2 bg-background"
            value={ioDirection || 'call'}
            onChange={(e) => onUpdate('ioDirection', e.target.value)}
          >
            <option value="call">Call — outbound request or invocation</option>
            <option value="receive">Receive — inbound event or payload</option>
            <option value="send">Send — outbound delivery or hand-off</option>
          </select>
          {ioDirection && IO_DIRECTION_HINTS[ioDirection as BlockIoDirection] && (
            <p className="text-[10px] text-muted-foreground mt-1">
              {IO_DIRECTION_HINTS[ioDirection as BlockIoDirection]}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
